import React, { FC, ReactNode, useMemo, useState } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';

import Flight from '@mui/icons-material/Flight';
import Hotel from '@mui/icons-material/Hotel';
import { BaseStyleEnums, CoreUtils, ShortCodeEnums, CoreCommonMessages, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { GameCenterIcon, PromotionIcon, TicketsIcon, WatchIcon } from '~/core/customIcons';
import { GAME_TIME } from '~/core/dateTimeFormats';
import { CountryCode } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import {
    filterBroadcasters,
    formatBroadcasters,
    getEnvironmentAwareHref,
    parseValueFromLocalizedAttribute,
} from '~/core/utils';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import { useGetWebStreamURLQuery } from '~/redux/ducks/webStreamURL';
import GameScore from '~/views/components/gameScore/GameScore';
import { NeutralSiteIndicator } from '~/views/pages/schedule/components/neutralSiteIndicator/NeutralSiteIndicator';
import SpecialEventMatchup from '~/views/pages/schedule/components/specialEventMatchup/SpecialEventMatchup';
import { formatMessageOverflow } from '~/views/pages/schedule/utils';

import { CalendarTeam, ScheduleGame } from '../../../../../../types';
import { MobileDay } from '../mobileDay/MobileDay';
import { getHotelInformation, getPlaneInformation, getPromoInformation, getScoringLinkFlags } from '../utils';

import { formatGameResult } from './utils';

import {
    BottomBar,
    Broadcasters,
    DayContainer,
    Divider,
    FinalGameScore,
    GameContainer,
    GameListContainer,
    GameListTime,
    GameTime,
    ImageAndInfoContainer,
    InformationContainer,
    LinkContainer,
    LinksContainer,
    LiveGame,
    LiveGameScore,
    NeutralSiteContainer,
    ScrollingText,
    SpecialEventImage,
    SpecialEventImageContainer,
    TeamLogo,
    TopBar,
} from './styles';

type DayProps = {
    day: string | null;
    games?: Array<ScheduleGame>;
    team: ShortCodeEnums.TeamTriCode;
    timezone: TimeZoneEnums;
};

const Day: FC<DayProps> = ({ day, games, team, timezone }) => {
    const intl = useIntl();
    const isFrench = intl.locale === ShortCodeEnums.Language.French;
    const isWebview = useIsWebview();

    const sortedGames: Array<ScheduleGame> | undefined = useMemo(() => {
        if (games !== undefined) {
            if (games.length === 1) {
                return games;
            }

            return [...games].sort((gameA, gameB) => {
                const isGameAHome = gameA.homeTeam.abbrev === team;
                const isGameBHome = gameB.homeTeam.abbrev === team;

                const gameAShouldShowTickets = gameA.ticketsLink !== undefined && isGameAHome;
                const gameBShouldShowTickets = gameB.ticketsLink !== undefined && isGameBHome;

                if (gameAShouldShowTickets && !gameBShouldShowTickets) {
                    return -1;
                } else if (gameBShouldShowTickets && !gameAShouldShowTickets) {
                    return 1;
                }

                const gameAStartMillis = DateTime.fromISO(gameA.startTimeUTC).toMillis();
                const gameBStartMillis = DateTime.fromISO(gameB.startTimeUTC).toMillis();

                if (gameAStartMillis === gameBStartMillis) {
                    if (isGameAHome) {
                        return -1;
                    } else if (isGameBHome) {
                        return 1;
                    }
                }

                return gameAStartMillis - gameBStartMillis;
            });
        }

        return undefined;
    }, [games, team]);

    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const size = useSizeWithThrottle();

    const [linkDescription, setLinkDescription] = useState<ReactNode>();
    const [selectedGameIndex, setSelectedGameIndex] = useState(0);
    const [shouldScrollLinks, setShouldScrollLinks] = useState<boolean>(false);

    const game = sortedGames ? sortedGames[selectedGameIndex] : undefined;

    const { data: streamUrl } = useGetWebStreamURLQuery(
        {
            broadcasts: game?.tvBroadcasts || [],
            countryCode,
            language: intl.locale as ShortCodeEnums.Language,
        },
        { skip: countryCode === CountryCode.None || game === undefined }
    );

    const threeMinuteRecap = useOptionForLocale({
        [ShortCodeEnums.Language.English]: game?.threeMinRecap,
        [ShortCodeEnums.Language.French]: game?.threeMinRecapFr,
    });

    const ticketsLink = useOptionForLocale({
        [ShortCodeEnums.Language.English]: game?.ticketsLink,
        [ShortCodeEnums.Language.French]: game?.ticketsLinkFr,
    });

    const isLargePlusDisplay = size.width > BaseStyleEnums.ScreenWidthBreakpoints.large;
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;
    const preGame = game ? CoreUtils.isPregame(game.gameState) : false;
    const liveGame = game ? CoreUtils.isGameLive(game.gameState) : false;
    const postGame = game ? CoreUtils.isPostGame(game.gameState) : false;
    const isHomeGame = game?.homeTeam.abbrev === team;
    const isSingleGameDay = sortedGames ? sortedGames.length === 1 : true;
    const planeInformation = getPlaneInformation(isHomeGame, game);
    const hotelInformation = getHotelInformation(isHomeGame, game);
    const promoInformation = getPromoInformation(isHomeGame, game);
    const messageCharacterCutoff = isLargePlusDisplay ? 15 : 8;
    const broadcasters = filterBroadcasters(game?.tvBroadcasts || [], countryCode);

    const scrollBroadcasters: boolean = useMemo(
        () => (isLargePlusDisplay ? broadcasters.length >= 3 : broadcasters.length > 2),
        [broadcasters, isLargePlusDisplay]
    );

    const colorProps = {
        singleGameDay: isSingleGameDay,
        hasGame: game !== undefined,
        neutralSite: game?.neutralSite,
        teamAbbrev: team,
        isHome: sortedGames?.[selectedGameIndex]?.homeTeam?.abbrev === team,
    };

    const getDay = (date: string | null): string => (date ? DateTime.fromISO(date).day.toString() : '');

    const getOpponentName = (homeTeamAbbrev: string, awayTeamAbbrev: string): ReactNode =>
        homeTeamAbbrev === team ? (
            <>
                <FormattedMessage {...CommonMessages.gameLocationDescriptions.abbreviations.versus} /> {awayTeamAbbrev}
            </>
        ) : (
            <>
                <FormattedMessage {...CommonMessages.gameLocationDescriptions.abbreviations.at} /> {homeTeamAbbrev}
            </>
        );

    const getTeamLogo = (team: CalendarTeam, home: boolean): string => {
        return home ? team.darkLogo : team.logo;
    };

    // TODO: make these scroll so we can remove formatMessageOverflow altogether
    const getGameTitle = (game: ScheduleGame, messageCharacterCutoff: number): ReactNode => {
        if (game.specialEvent?.name) {
            return formatMessageOverflow(
                messageCharacterCutoff,
                parseValueFromLocalizedAttribute(game.specialEvent.name)
            );
        } else {
            return (game.homeTeam.abbrev === team && game.homeTeam.homeSplitSquad) ||
                (game.awayTeam.abbrev === team && game.awayTeam.awaySplitSquad) ? (
                <FormattedMessage {...CommonMessages.gameTypeDescriptions.long.splitSquad} />
            ) : (
                formatMessageOverflow(messageCharacterCutoff, parseValueFromLocalizedAttribute(game.venue))
            );
        }
    };

    const updateLinkDescriptionAndScroll = (isLarge: boolean, isListView: boolean, partnerMessage?: string) => {
        const cutoff = isListView ? (isLarge ? 15 : 8) : isLarge ? 20 : 15;
        if (partnerMessage) {
            setShouldScrollLinks(partnerMessage.length > cutoff);
            setLinkDescription(partnerMessage);
        }
    };

    const clearLinkDescription = () => {
        setShouldScrollLinks(false);
        setLinkDescription(null);
    };

    const gameLinks = useMemo(() => {
        const components: Array<ReactNode> = [];
        if (!game) {
            return [];
        }

        const scoringLinkFlags = getScoringLinkFlags(game, team, streamUrl, threeMinuteRecap);
        if (scoringLinkFlags.isTicketsLinkShown) {
            components.push(
                <LinkContainer
                    key={`${game?.id}-ticket-link`}
                    href={ticketsLink}
                    onMouseEnter={() =>
                        setLinkDescription(<FormattedMessage {...CommonMessages.linkDescriptions.tickets} />)
                    }
                    onMouseLeave={() => clearLinkDescription()}
                    aria-label={intl.formatMessage(CommonMessages.ariaLabels.links.tickets)}
                >
                    <TicketsIcon />
                </LinkContainer>
            );
            components.push(<Divider key={`${game?.id}-ticket-link-divider`} />);
        }

        if (scoringLinkFlags.isPromoLinkShown && promoInformation.promoMessage) {
            components.push(
                <LinkContainer
                    key={`${game?.id}-promo-link`}
                    href={promoInformation.promoLink}
                    onMouseEnter={() =>
                        updateLinkDescriptionAndScroll(isLargePlusDisplay, false, promoInformation.promoMessage)
                    }
                    onMouseLeave={() => clearLinkDescription()}
                    aria-label={intl.formatMessage(CommonMessages.ariaLabels.links.promo)}
                >
                    <PromotionIcon />
                </LinkContainer>
            );
            components.push(<Divider key={`${game?.id}-promo-link-divider`} />);
        }

        if (scoringLinkFlags.isPlaneLinkShown) {
            components.push(
                <LinkContainer
                    key={`${game?.id}-plane-link`}
                    href={planeInformation.planeLink}
                    onMouseEnter={() =>
                        updateLinkDescriptionAndScroll(isLargePlusDisplay, false, planeInformation.planePartner)
                    }
                    onMouseLeave={() => clearLinkDescription()}
                    aria-label={intl.formatMessage(CommonMessages.ariaLabels.links.plane)}
                >
                    <Flight />
                </LinkContainer>
            );
            components.push(<Divider key={`${game?.id}-plane-link-divider`} />);
        }

        if (scoringLinkFlags.isHotelLinkShown) {
            components.push(
                <LinkContainer
                    key={`${game?.id}-hotel-link`}
                    href={hotelInformation.hotelLink}
                    onMouseEnter={() =>
                        updateLinkDescriptionAndScroll(isLargePlusDisplay, false, hotelInformation.hotelPartner)
                    }
                    onMouseLeave={() => clearLinkDescription()}
                    aria-label={intl.formatMessage(CommonMessages.ariaLabels.links.hotel)}
                >
                    <Hotel />
                </LinkContainer>
            );
            components.push(<Divider key={`${game?.id}-hotel-link-divider`} />);
        }

        if (scoringLinkFlags.isGameCenterLinkShown && game?.gameCenterLink) {
            components.push(
                <LinkContainer
                    key={`${game?.id}-gamecenter-link`}
                    href={getEnvironmentAwareHref(game.gameCenterLink, isWebview)}
                    onMouseEnter={() =>
                        setLinkDescription(<FormattedMessage {...CommonMessages.linkDescriptions.gamecenter} />)
                    }
                    onMouseLeave={() => clearLinkDescription()}
                    aria-label={intl.formatMessage(CommonMessages.ariaLabels.links.gamecenter)}
                >
                    <GameCenterIcon />
                </LinkContainer>
            );
            components.push(<Divider key={`${game?.id}-gamecenter-link-divider`} />);
        }

        if (scoringLinkFlags.isStreamLinkShown) {
            components.push(
                <LinkContainer
                    key={`${game?.id}-stream-link`}
                    href={streamUrl}
                    onMouseEnter={() =>
                        setLinkDescription(<FormattedMessage {...CommonMessages.linkDescriptions.stream} />)
                    }
                    onMouseLeave={() => clearLinkDescription()}
                    aria-label={intl.formatMessage(CommonMessages.ariaLabels.links.stream)}
                >
                    <WatchIcon />
                </LinkContainer>
            );
            components.push(<Divider key={`${game?.id}-stream-recap-link-divider`} />);
        }

        if (scoringLinkFlags.isThreeMinuteRecapShown) {
            components.push(
                <LinkContainer
                    key={`${game?.id}-recap-link`}
                    href={threeMinuteRecap}
                    onMouseEnter={() =>
                        setLinkDescription(<FormattedMessage {...CommonMessages.linkDescriptions.recap} />)
                    }
                    onMouseLeave={() => clearLinkDescription()}
                    aria-label={intl.formatMessage(CommonMessages.ariaLabels.links.recap)}
                >
                    <WatchIcon />
                </LinkContainer>
            );
            components.push(<Divider key={`${game?.id}-stream-recap-link-divider`} />);
        }

        return components;
    }, [
        game,
        intl,
        isLargePlusDisplay,
        planeInformation,
        hotelInformation,
        promoInformation,
        streamUrl,
        team,
        threeMinuteRecap,
        ticketsLink,
    ]);

    return (
        <DayContainer $colorProps={colorProps}>
            {isMedium ? (
                <>
                    <TopBar $colorProps={colorProps} $isHidden={!day || !isMedium} aria-label={day || undefined}>
                        {getDay(day)}
                        {sortedGames?.length === 1 && (
                            <LiveGame hidden={!liveGame} $isFrench={isFrench}>
                                <FormattedMessage
                                    {...CoreCommonMessages.gameStateDescriptions.long[ShortCodeEnums.GameState.live]}
                                />
                            </LiveGame>
                        )}
                    </TopBar>
                    {sortedGames && sortedGames.length === 1 && (
                        <ImageAndInfoContainer $isHidden={!game} $postGame={postGame}>
                            {game?.specialEvent ? (
                                <SpecialEventImageContainer $hasSpecialEventImage={!!game.specialEvent?.lightLogoUrl}>
                                    {game.specialEvent?.lightLogoUrl ? (
                                        <SpecialEventImage
                                            src={parseValueFromLocalizedAttribute(
                                                game.specialEvent.lightLogoUrl,
                                                intl.locale
                                            )}
                                        />
                                    ) : (
                                        <SpecialEventMatchup
                                            intl={intl}
                                            game={game}
                                            specialEvent={game.specialEvent}
                                            isCalendarView
                                            allowTruncatedText
                                        />
                                    )}
                                    {game?.neutralSite && (
                                        <NeutralSiteContainer $hasSpecialEventImage={!!game.specialEvent?.lightLogoUrl}>
                                            <NeutralSiteIndicator />
                                        </NeutralSiteContainer>
                                    )}
                                </SpecialEventImageContainer>
                            ) : (
                                <TeamLogo>
                                    {game !== undefined && (
                                        <TeamDisplay
                                            logoUrlOverride={getTeamLogo(
                                                game && game.homeTeam.abbrev === team ? game.awayTeam : game.homeTeam,
                                                sortedGames?.[selectedGameIndex].homeTeam.abbrev === team
                                            )}
                                            team={{
                                                triCode:
                                                    team === game?.homeTeam.abbrev
                                                        ? game?.awayTeam.abbrev
                                                        : game.homeTeam.abbrev,
                                            }}
                                            displayName={false}
                                        />
                                    )}
                                </TeamLogo>
                            )}
                            <InformationContainer>
                                <GameTime $colorProps={colorProps} $isHidden={!preGame}>
                                    {game &&
                                        (game.gameScheduleState === ShortCodeEnums.GameScheduleState.scheduled ? (
                                            game.startTimeUTC &&
                                            DateTime.fromISO(game.startTimeUTC)
                                                .setZone(
                                                    timezone === TimeZoneEnums.VENUE ? game.venueTimezone : timezone
                                                )
                                                .toLocaleString(GAME_TIME)
                                        ) : (
                                            <CoreUtils.SafeFormattedMessage
                                                message={
                                                    CoreCommonMessages.gameScheduleStateDescriptions.long[
                                                        game.gameScheduleState
                                                    ]
                                                }
                                            />
                                        ))}
                                </GameTime>
                                <LiveGameScore $isHidden={!liveGame} $colorProps={colorProps}>
                                    {liveGame && game !== undefined && (
                                        <GameScore
                                            homeTeamAbbrev={game.homeTeam.abbrev}
                                            awayTeamAbbrev={game.awayTeam.abbrev}
                                            homeTeamScore={game.homeTeam.score}
                                            awayTeamScore={game.awayTeam.score}
                                        />
                                    )}
                                </LiveGameScore>
                                <FinalGameScore $colorProps={colorProps} $isHidden={!postGame}>
                                    {postGame && game !== undefined && (
                                        <>
                                            {formatGameResult(
                                                game.awayTeam.abbrev,
                                                game.homeTeam.abbrev,
                                                team,
                                                game.awayTeam.score,
                                                game.homeTeam.score
                                            )}
                                            {game?.gameOutcome?.lastPeriodType !==
                                                ShortCodeEnums.PeriodType.regulation && (
                                                <>
                                                    &nbsp; (
                                                    {CoreUtils.getPeriodDisplayFromDescriptor(
                                                        intl,
                                                        game.periodDescriptor,
                                                        game.gameType
                                                    )}
                                                    )
                                                </>
                                            )}
                                        </>
                                    )}
                                </FinalGameScore>
                                <Broadcasters $isHidden={postGame}>
                                    <ScrollingText $colorProps={colorProps} $shouldScroll={scrollBroadcasters}>
                                        {formatBroadcasters(broadcasters, true)}
                                    </ScrollingText>
                                </Broadcasters>
                            </InformationContainer>
                        </ImageAndInfoContainer>
                    )}
                    {sortedGames && sortedGames.length > 1 && (
                        <GameListContainer>
                            {sortedGames.map((game, index) => (
                                <GameContainer
                                    key={game.id}
                                    $isSelectedGame={index === selectedGameIndex}
                                    onClick={() => setSelectedGameIndex(index)}
                                >
                                    {getGameTitle(game, messageCharacterCutoff)}
                                    <GameListTime>
                                        {isLargePlusDisplay ? (
                                            <FormattedMessage
                                                description="A message displaying the opponent team and time of game"
                                                defaultMessage="{opponent} - {gameTime}"
                                                values={{
                                                    opponent: getOpponentName(
                                                        game.homeTeam.abbrev,
                                                        game.awayTeam.abbrev
                                                    ),
                                                    gameTime:
                                                        game.startTimeUTC &&
                                                        game.gameScheduleState ===
                                                            ShortCodeEnums.GameScheduleState.scheduled ? (
                                                            DateTime.fromISO(game.startTimeUTC)
                                                                .setZone(timezone)
                                                                .toLocaleString(GAME_TIME)
                                                        ) : (
                                                            <CoreUtils.SafeFormattedMessage
                                                                message={
                                                                    CoreCommonMessages.gameScheduleStateDescriptions
                                                                        .long[game.gameScheduleState]
                                                                }
                                                            />
                                                        ),
                                                }}
                                            />
                                        ) : (
                                            getOpponentName(game.homeTeam.abbrev, game.awayTeam.abbrev)
                                        )}
                                    </GameListTime>
                                    {game.neutralSite && <NeutralSiteIndicator />}
                                </GameContainer>
                            ))}
                        </GameListContainer>
                    )}
                    <BottomBar $colorProps={colorProps}>
                        <ScrollingText $colorProps={colorProps} $shouldScroll={shouldScrollLinks}>
                            {linkDescription}
                        </ScrollingText>
                        <LinksContainer
                            $isHidden={game === undefined || gameLinks.length === 0}
                            $shrinkLinks={gameLinks.length > 7}
                        >
                            {game && gameLinks}
                        </LinksContainer>
                    </BottomBar>
                </>
            ) : sortedGames ? (
                <MobileDay gameList={sortedGames} dayOfMonth={getDay(day)} team={team} timezone={timezone} />
            ) : (
                <></>
            )}
        </DayContainer>
    );
};

export default Day;
